<template>
  <div class="detail-card">
    <h2 class="detail-name">{{ pair.name }}</h2>

    <div class="rel-header">
      <div v-if="relationTextRef" class="rel-caption">{{ relationTextRef }}</div>
      <div class="rel-squares">
        <span v-for="(step, i) in relSquaresRef" :key="i" class="rel-square" :class="step"></span>
      </div>
    </div>

    <div v-if="hasSummaryRef" class="detail-summary">
      <div v-if="pair.childOrder && pair.siblingsCount" class="summary-item">
        <div class="big-num">{{ pair.childOrder }}<sup>{{ ordinalSuffix(pair.childOrder) }}</sup></div>
        <div class="summary-sub">of {{ pair.siblingsCount }} children</div>
      </div>
      <div v-if="pair.flag" class="summary-flag">
        <span class="fi" :class="`fi-${pair.flag}`"></span>
      </div>
      <div v-if="pair.childCount" class="summary-item">
        <div class="big-num">{{ pair.childCount }}</div>
        <div class="summary-sub">children</div>
      </div>
    </div>

    <p v-if="notesTextRef" class="detail-notes">{{ notesTextRef }}</p>

    <ul class="detail-events">
      <li v-for="(detail, i) in pair.details" :key="i" class="event-row" :class="{ highlight: detail.highlight }">
        <div class="ev-date">
          <div class="ev-year">{{ dateYearPart(detail.date) }}</div>
          <div v-if="dateDayMonthPart(detail.date)" class="ev-daymonth">{{ dateDayMonthPart(detail.date) }}</div>
        </div>
        <div class="ev-age">{{ ageAt(pair, detail.date) }}</div>
        <div class="ev-body">
          <div class="ev-label">{{ eventLabel(detail) }}</div>
          <div v-if="detail.place" class="ev-place">{{ detail.place }}</div>
        </div>
        <div class="ev-flag">
          <span v-if="detail.flag" class="fi" :class="`fi-${detail.flag}`"></span>
        </div>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.detail-card {
  width: 100%;
  height: 100%;
  overflow: hidden; // crop long event lists for now
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 16px;
  box-shadow: 0 5px 22px rgba(0, 0, 0, 0.25);
  padding: 3.4cqi 4.2cqi;
  display: flex;
  flex-direction: column;
  gap: 1.4cqi;
}

.rel-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1cqi;
}

// "Kent's grandfather" / "Marilyn's great-grandmother" -- sits just under the person's name
.rel-caption {
  font-size: 3.6cqi;
  color: #666;
  text-align: center;
}

.rel-squares {
  display: flex;
  justify-content: center;
  gap: 1cqi;
}

.rel-square {
  width: 3.4cqi;
  height: 3.4cqi;
  border-radius: 0.5cqi;

  &.f {
    background: #73a7f0;
  }

  &.m {
    background: #f2c1d6;
  }
}

.detail-name {
  text-align: center;
  font-size: 5.5cqi;
  font-weight: bold;
  margin: 0;
}

.detail-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5.5cqi;
  margin: 4cqi 0 2.1cqi;

  .summary-item {
    text-align: center;
  }

  .big-num {
    font-size: 7cqi;
    line-height: 1;

    sup {
      font-size: 3.4cqi;
    }
  }

  .summary-sub {
    color: #666;
    font-size: 3.4cqi;
  }

  .summary-flag .fi {
    font-size: 7.6cqi;
    box-shadow: 0 0 0 1px #eee;
  }
}

.detail-notes {
  margin: 0;
  text-align: left;
  font-size: 3.4cqi;
  line-height: 1.3;
  white-space: pre-wrap;
}

.detail-events {
  list-style: none;
  margin: 0;
  padding: 0;
  flex-grow: 1;
  overflow: hidden;
}

.event-row {
  display: grid;
  grid-template-columns: 11cqi 5.5cqi 1fr auto;
  column-gap: 2.1cqi;
  align-items: baseline;
  padding: 0.9cqi 1cqi;
  font-size: 3.4cqi;

  &.highlight {
    background: #fff176;
    border-radius: 0.7cqi;
  }

  .ev-date {
    text-align: end;
    white-space: nowrap;
    line-height: 1.05;
  }

  .ev-year {
    font-size: 3.7cqi;
  }

  // day + month sit just under the year, smaller and quieter
  .ev-daymonth {
    font-size: 3cqi;
    color: #666;
  }

  .ev-age {
    text-align: end;
    color: #666;
  }

  .ev-body {
    line-height: 1.25;
  }

  .ev-place {
    color: #666;
    font-size: 3cqi;
    margin-top: -0.26cqi;
  }

  .ev-flag {
    align-self: center;

    .fi {
      font-size: 3.8cqi;
      box-shadow: 0 0 0 1px #eee;
    }
  }
}
</style>

<script setup lang="ts">
import { computed } from 'vue'
import { MatchingCardPair } from './MatchingGameDefinition'
import { ageAt, dateDayMonthPart, dateYearPart, eventLabel, ordinalSuffix } from './MatchingGameHelpers'
import { getAncestorRelationText } from '@/rd/RelationPath'
import { Gender } from '@/rd/ResearchDataModel'

const props = defineProps<{ pair: MatchingCardPair }>()

const relSquaresRef = computed(() =>
  [...(props.pair.relationship || '')].map(step => (step === 'F' ? 'f' : 'm')))

// "Kent's grandfather" / "Marilyn's great-grandmother". The first square is the root couple member
// this line ascends from (F = Kent, M = Marilyn), so generations-up = length - 1; the final square
// gives the ancestor's gender. Names are hardcoded for now (matches the setup intro caption).
const relationTextRef = computed(() => {
  const rel = props.pair.relationship || ''
  const generations = rel.length - 1
  if (generations < 1) {
    return ''
  }
  const subject = rel[0] === 'F' ? 'Kent' : 'Marilyn'
  const gender = rel.at(-1) === 'F' ? Gender.Male : Gender.Female
  return `${subject}'s ${getAncestorRelationText(generations, gender)}`
})

const hasSummaryRef = computed(() =>
  !!(props.pair.childOrder && props.pair.siblingsCount) || !!props.pair.flag || !!props.pair.childCount)

// the card's primary text followed by any notes, joined with a space (either may be empty). When
// both are present and the primary text lacks terminal punctuation, add a period so the two read as
// separate sentences.
const notesTextRef = computed(() => {
  const text = props.pair.text?.trim() ?? ''
  const notes = props.pair.notes?.trim() ?? ''
  if (!text || !notes) {
    return text || notes
  }
  const punctuated = /[.!?]$/.test(text) ? text : `${text}.`
  return `${punctuated} ${notes}`
})
</script>
